import { useEffect, useRef, useState } from "react";
import { readTheme, resolveTheme } from "../lib/theme.js";

/* Google Identity Services, loaded only when a client ID exists.
 *
 * The browser's whole part in Google sign-in is to obtain an ID token and
 * hand it to our API; the token is verified server-side against Google's
 * published keys, so nothing here decides who gets in. FedCM is left on
 * (the default) so the button keeps working as third-party cookies go. */

const SRC = "https://accounts.google.com/gsi/client";
let loading = null;

function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="' + SRC + '"]');
    const el = existing || document.createElement("script");
    el.src = SRC;
    el.async = true;
    el.defer = true;
    el.addEventListener("load", () => resolve());
    el.addEventListener("error", () => {
      loading = null;
      reject(new Error("Google sign-in could not be loaded."));
    });
    if (!existing) document.head.appendChild(el);
  });
  return loading;
}

/* Google's button is drawn by Google, in one of its own themes, and we only
 * get to say which. The two we use are the ones meant for the background
 * they sit on: `filled_black` is a near-black button, made to stand out on
 * a light page, and `outline` is a white one for a dark page.
 *
 * Picking one and leaving it is what went wrong before: `filled_black` on
 * the dark palette put #202124 on a #121b2f card, a contrast ratio of
 * 1.07:1 against the 3:1 a control needs — the button was there, and you
 * could not see it. */
function googleTheme(mode) {
  return mode === "light" ? "filled_black" : "outline";
}

/* Which palette is actually on screen. The login page carries no theme
   toggle, so this is settled at mount — except for "system", which can
   change under a page that is just sitting there. */
function useResolvedTheme() {
  const [mode, setMode] = useState(() => resolveTheme(readTheme()));

  useEffect(() => {
    if (readTheme() !== "system") return;
    const query = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => setMode(resolveTheme("system"));
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return mode;
}

export function GoogleButton({ clientId, onCredential, onError, disabled }) {
  const host = useRef(null);
  const [failed, setFailed] = useState("");
  const mode = useResolvedTheme();
  /* The callback is read from a ref so re-rendering the page does not tear
     down and re-init the Google button mid-sign-in. */
  const handler = useRef(onCredential);
  handler.current = onCredential;
  const errored = useRef(onError);
  errored.current = onError;

  useEffect(() => {
    if (!clientId) return;
    let live = true;

    loadGis()
      .then(() => {
        if (!live || !host.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response?.credential) handler.current(response.credential);
            else errored.current?.("Google sent no credential.");
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          context: "signin",
        });
        host.current.innerHTML = "";
        window.google.accounts.id.renderButton(host.current, {
          type: "standard",
          /* Google draws the button once, so a theme change has to redraw
             it — which is why `mode` is a dependency of this effect. */
          theme: googleTheme(mode),
          size: "large",
          shape: "rectangular",
          text: "signin_with",
          logo_alignment: "left",
          width: 280,
        });
      })
      .catch((e) => {
        /* A load failure is reported where the button would have been, and
           nowhere else: bubbling it to the page as well printed the same
           sentence twice. */
        if (live) setFailed(e.message);
      });

    return () => {
      live = false;
    };
    /* onError is read through a ref, so re-rendering the page does not
       re-initialise Google's button mid-sign-in. */
  }, [clientId, mode]);

  if (!clientId) return null;

  if (failed) {
    return (
      <p className="errline" role="alert">
        {failed} Check your connection and reload — Google sign-in is the
        only way in.
      </p>
    );
  }

  return (
    <div className={"gbtn" + (disabled ? " is-busy" : "")}>
      <div ref={host} />
    </div>
  );
}
